/**
 * CurrencyConversionBadge Component
 * 
 * Badge informativo que indica quando há conversão de moedas acontecendo.
 * Usado quando múltiplas empresas com moedas diferentes estão selecionadas.
 * 
 * @example
 * ```tsx
 * <CurrencyConversionBadge 
 *   baseCurrency="EUR"
 *   currencies={['EUR', 'BRL', 'USD']}
 *   companiesByCurrency={mapOfCompanies}
 * />
 * ```
 */

import { ArrowRightLeft, Info } from 'lucide-react';
import { Badge } from '../ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { CURRENCIES } from '../../lib/constants/currencies';
import type { Currency } from '../../types/currency';

export interface CurrencyConversionBadgeProps {
  /** Moeda base para consolidação */
  baseCurrency: Currency;
  
  /** Lista de todas as moedas presentes */
  currencies: Currency[];
  
  /** Mapa de empresas agrupadas por moeda */
  companiesByCurrency: Map<Currency, Array<{ id: string; name: string }>>;
  
  /** Estilo do badge */
  variant?: 'default' | 'secondary' | 'outline';
  
  /** Classe CSS adicional */
  className?: string;
}

export function CurrencyConversionBadge({
  baseCurrency,
  currencies,
  companiesByCurrency,
  variant = 'secondary',
  className,
}: CurrencyConversionBadgeProps) {
  // Se há apenas uma moeda, não mostrar badge
  if (currencies.length <= 1) {
    return null;
  }

  const baseCurrencyInfo = CURRENCIES[baseCurrency];
  const otherCurrencies = currencies.filter((c) => c !== baseCurrency);

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <Badge 
            variant={variant} 
            className={`gap-1.5 cursor-help ${className || ''}`}
          >
            <ArrowRightLeft className="h-3 w-3" />
            <span className="hidden sm:inline">
              Consolidado em {baseCurrencyInfo.symbol}
            </span>
            <span className="sm:hidden">
              {baseCurrencyInfo.symbol}
            </span>
          </Badge>
        </TooltipTrigger>
        
        <TooltipContent side="bottom" className="max-w-xs p-3">
          <div className="space-y-2">
            {/* Título */}
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-sm">Conversão Automática</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Valores consolidados em {baseCurrencyInfo.name} ({baseCurrencyInfo.symbol})
                </p>
              </div>
            </div>

            {/* Separador */}
            <div className="border-t border-border" />

            {/* Moedas por empresa */}
            <div className="space-y-1.5">
              {Array.from(companiesByCurrency.entries()).map(([currency, companies]) => {
                const currencyInfo = CURRENCIES[currency];
                const isBase = currency === baseCurrency;

                return (
                  <div key={currency} className="text-xs">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="font-medium">
                        {currencyInfo.symbol} {currencyInfo.code}
                      </span>
                      {isBase && (
                        <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                          Base
                        </Badge>
                      )}
                      {!isBase && (
                        <ArrowRightLeft className="h-3 w-3 text-muted-foreground" />
                      )}
                    </div>
                    <ul className="list-disc list-inside text-muted-foreground pl-1">
                      {companies.map((company) => (
                        <li key={company.id} className="truncate">
                          {company.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>

            {/* Nota sobre conversão */}
            {otherCurrencies.length > 0 && (
              <>
                <div className="border-t border-border" />
                <p className="text-[10px] text-muted-foreground">
                  💡 Taxas de câmbio atualizadas diariamente
                </p>
              </>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

